/**
 * Automatic interpretation generator for astrological charts
 * Generates interpretations based on planetary positions, aspects, and houses
 */

const zodiacSigns = {
  Aries: { element: 'Fire', quality: 'Cardinal', ruler: 'Mars', description: 'rohkea, impulsiivinen, itsenäinen' },
  Taurus: { element: 'Earth', quality: 'Fixed', ruler: 'Venus', description: 'vakaa, sinnikäs, materiaan kiintynyt' },
  Gemini: { element: 'Air', quality: 'Mutable', ruler: 'Mercury', description: 'utelias, kommunikatiivinen, monipuolinen' },
  Cancer: { element: 'Water', quality: 'Cardinal', ruler: 'Moon', description: 'tunteellinen, suojelushaluinen, intuitiivinen' },
  Leo: { element: 'Fire', quality: 'Fixed', ruler: 'Sun', description: 'ylpeä, luova, karismaattinen' },
  Virgo: { element: 'Earth', quality: 'Mutable', ruler: 'Mercury', description: 'analyyttinen, järjestelmällinen, palveleva' },
  Libra: { element: 'Air', quality: 'Cardinal', ruler: 'Venus', description: 'tasapainoinen, kaunis, yhteistyöhaluinen' },
  Scorpio: { element: 'Water', quality: 'Fixed', ruler: 'Mars/Pluto', description: 'syvä, intensiivinen, muuttumisen kykenevä' },
  Sagittarius: { element: 'Fire', quality: 'Mutable', ruler: 'Jupiter', description: 'avoin, filosofinen, seikkailunhaluinen' },
  Capricorn: { element: 'Earth', quality: 'Cardinal', ruler: 'Saturn', description: 'ambitioitunut, vastuullinen, kunnianhimoinen' },
  Aquarius: { element: 'Air', quality: 'Fixed', ruler: 'Uranus', description: 'itsenäinen, innovatiivinen, yhteisöllinen' },
  Pisces: { element: 'Water', quality: 'Mutable', ruler: 'Neptune', description: 'tunteellinen, intuitiivinen, unelias' }
};

const planets = {
  Sun: { description: 'Ydinidentiteetti, ego, tarkoitus', keywords: ['identiteetti', 'itseilmaisu', 'voima'] },
  Moon: { description: 'Tunteet, tarpeet, alitajunta', keywords: ['tunteet', 'turvallisuus', 'reaktiot'] },
  Mercury: { description: 'Ajattelu, viestintä, oppiminen', keywords: ['kommunikaatio', 'logiikka', 'analyytti'] },
  Venus: { description: 'Rakkaus, kauneus, arvot', keywords: ['rakkaus', 'kauneus', 'harmonia'] },
  Mars: { description: 'Energia, toiminta, halu', keywords: ['energia', 'aggressio', 'motivaatio'] },
  Jupiter: { description: 'Kasvu, laajentuminen, viisaus', keywords: ['kasvu', 'optimismi', 'filosofia'] },
  Saturn: { description: 'Rajoitukset, vastuu, kuri', keywords: ['rajoitukset', 'vastuu', 'kasvu'] },
  Uranus: { description: 'Muutos, kapina, innovaatio', keywords: ['muutos', 'itsenäisyys', 'innovaatio'] },
  Neptune: { description: 'Unelmat, intuitio, hengellisyys', keywords: ['unelmat', 'intuitio', 'henkisyys'] },
  Pluto: { description: 'Muutos, transformaatio, valta', keywords: ['muutos', 'transformaatio', 'syvyys'] }
};

const aspects = {
  Conjunction: { description: 'Yhdistää planeettojen energiat voimakkaasti', nature: 'neutral' },
  Sextile: { description: 'Harmoninen yhteys, luova potentiaali', nature: 'harmonious' },
  Square: { description: 'Haaste, jännite, kasvun mahdollisuus', nature: 'challenging' },
  Trine: { description: 'Harmoninen, luonnollinen virtaus', nature: 'harmonious' },
  Opposition: { description: 'Polariteetti, tasapainon tarve', nature: 'challenging' }
};

/**
 * Generate interpretation for Sun sign
 */
function generateSunSignInterpretation(sunSign) {
  const sign = zodiacSigns[sunSign];
  if (!sign) return null;

  return {
    title: `Aurinkomerkki: ${sunSign}`,
    content: `Aurinkomerkki ${sunSign} määrittää ytimen identiteetistäsi ja siitä, miten ilmaiset itseäsi maailmalle. ${sign.element}-elementin edustajana olet ${sign.description}. Tämä merkki antaa sinulle luonteenpiirteitä, jotka ovat keskeisiä persoonallisuudellesi. ${sign.ruler}-planeetta hallitsee merkkiäsi ja vaikuttaa siihen, miten ilmaiset voimaasi ja tarkoitustasi.`,
    interpretation_type: 'sun_sign'
  };
}

/**
 * Generate interpretation for Moon sign
 */
function generateMoonSignInterpretation(moonSign) {
  const sign = zodiacSigns[moonSign];
  if (!sign) return null;

  return {
    title: `Kuumerkki: ${moonSign}`,
    content: `Kuumerkki ${moonSign} paljastaa tunteidesi syvyyden ja sen, miten reagoit emotionaalisesti eri tilanteisiin. ${sign.element}-elementin vaikutus tekee sinusta ${sign.description}. Tämä merkki määrittää, miten tarvitset turvallisuutta ja miten ilmaiset tunteitasi. Kuumerkki vaikuttaa myös siihen, miten lepäät ja lataudut energiaa.`,
    interpretation_type: 'moon_sign'
  };
}

/**
 * Generate interpretation for Rising sign
 */
function generateRisingSignInterpretation(risingSign) {
  const sign = risingSign?.sign || risingSign;
  if (!sign || !zodiacSigns[sign]) return null;

  const signInfo = zodiacSigns[sign];
  return {
    title: `Nousumerkki: ${sign}`,
    content: `Nousumerkki ${sign} määrittää sen, miten muut näkevät sinut ja miten esiinny maailmalle. Tämä on "maskisi" - ensimmäinen vaikutelma, jonka annat. ${signInfo.element}-elementin vaikutus tekee sinusta ${signInfo.description}. Nousumerkki vaikuttaa myös siihen, miten lähestyt uusia tilanteita ja miten suhtaudut maailmaan. Se on osa sinua, jota muut näkevät ensin.`,
    interpretation_type: 'rising_sign'
  };
}

/**
 * Generate interpretation for planetary positions
 */
function generatePlanetaryInterpretations(positions) {
  const interpretations = [];

  // Generate interpretation for each planet
  for (const [planetName, position] of Object.entries(positions)) {
    if (!position || !position.sign) continue;

    const planet = planets[planetName];
    const sign = zodiacSigns[position.sign];
    if (!planet || !sign) continue;

    const degree = parseFloat(position.degree || 0);
    const degreeText = degree > 0 && degree < 1 ? 'hyvin varhaisessa' : 
                      degree > 29 ? 'loppupäässä' : 
                      degree > 15 ? 'keskivaiheilla' : 'alkupäässä';

    interpretations.push({
      title: `${planetName} ${position.sign}ssa`,
      content: `${planetName} ${position.sign}ssa ${degreeText} (${position.degree}°) tuo ${planet.description} merkityksellä. ${sign.element}-elementin vaikutus tekee tästä planeettasijainnista ${sign.description}. Tämä yhdistelmä vaikuttaa siihen, miten ilmaiset ${planet.keywords.join(', ')} -puoliasi elämässäsi.`,
      interpretation_type: 'planet_position'
    });
  }

  return interpretations;
}

/**
 * Generate interpretation for aspects
 */
function generateAspectInterpretations(aspects) {
  if (!aspects || aspects.length === 0) return [];

  const interpretations = [];
  const majorAspects = aspects.slice(0, 5); // Top 5 aspects

  for (const aspect of majorAspects) {
    const aspectInfo = aspects[aspect.aspect];
    if (!aspectInfo) continue;

    const nature = aspectInfo.nature === 'harmonious' ? 'harmoninen' : 'haastava';
    const planet1 = planets[aspect.planet1]?.description || aspect.planet1;
    const planet2 = planets[aspect.planet2]?.description || aspect.planet2;

    interpretations.push({
      title: `${aspect.planet1} ${aspect.aspect} ${aspect.planet2}`,
      content: `${aspect.aspect}-aspekti ${aspect.planet1} ja ${aspect.planet2} välillä on ${nature} yhteys. ${aspectInfo.description} Tämä aspekti vaikuttaa siihen, miten ${planet1} ja ${planet2} vuorovaikuttavat keskenään syntymäkartassasi. Aspektin orb on ${aspect.orb}°, mikä tekee siitä ${Math.abs(parseFloat(aspect.orb)) < 2 ? 'erittäin tarkan' : 'kohtalaisen tarkan'}.`,
      interpretation_type: 'aspect'
    });
  }

  return interpretations;
}

/**
 * Generate house interpretations
 */
function generateHouseInterpretations(houses, risingSign) {
  if (!houses) return [];

  const houseMeanings = {
    'House 1': 'Identiteetti, ulkoasu, ensimmäinen vaikutelma',
    'House 2': 'Talous, materia, arvot, omistaminen',
    'House 3': 'Kommunikaatio, veljet/siskot, lyhyet matkat',
    'House 4': 'Koti, perhe, juuret, sisäinen turvallisuus',
    'House 5': 'Luovuus, rakastaminen, lapset, viihde',
    'House 6': 'Työ, terveys, rutiinit, palvelu',
    'House 7': 'Kumppanuus, avioliitto, yhteistyö',
    'House 8': 'Muutos, yhteiset varat, syvyys, tabut',
    'House 9': 'Filosofia, matkustelu, korkeakoulutus, uskonto',
    'House 10': 'Ura, maine, julkisuus, tavoitteet',
    'House 11': 'Ystävät, toiveet, yhteisöt, innovaatio',
    'House 12': 'Alitajunta, salaisuudet, hengellisyys, eristäytyminen'
  };

  const interpretations = [];

  for (let i = 1; i <= 12; i++) {
    const houseKey = `House ${i}`;
    const houseSign = houses[houseKey];
    if (!houseSign) continue;

    const sign = zodiacSigns[houseSign];
    if (!sign) continue;

    interpretations.push({
      title: `${i}. Huone: ${houseSign}`,
      content: `${i}. huone edustaa ${houseMeanings[houseKey]}. Huoneen merkki ${houseSign} tuo ${sign.element}-elementin energian tähän elämänalueeseen, mikä tekee siitä ${sign.description}. Tämä vaikuttaa siihen, miten koet ja ilmaiset tämän huoneen aiheita elämässäsi.`,
      interpretation_type: 'house'
    });
  }

  return interpretations;
}

/**
 * Generate overview interpretation
 */
function generateOverviewInterpretation(chartData) {
  const { sunSign, moonSign, risingSign } = chartData;
  const risingSignName = risingSign?.sign || risingSign;

  if (!sunSign || !moonSign) return null;

  const sunElement = zodiacSigns[sunSign]?.element || '';
  const moonElement = zodiacSigns[moonSign]?.element || '';
  const risingElement = zodiacSigns[risingSignName]?.element || '';

  const elements = [sunElement, moonElement, risingElement].filter(Boolean);
  const elementCounts = elements.reduce((acc, el) => {
    acc[el] = (acc[el] || 0) + 1;
    return acc;
  }, {});

  const dominantElement = Object.keys(elementCounts).reduce((a, b) => 
    elementCounts[a] > elementCounts[b] ? a : b, Object.keys(elementCounts)[0]);

  return {
    title: 'Yleiskuvaus syntymäkartastasi',
    content: `Syntymäkarttasi "Big Three" - Aurinkomerkki ${sunSign}, Kuumerkki ${moonSign} ja Nousumerkki ${risingSignName} - luovat ainutlaatuisen yhdistelmän persoonallisuudestasi. ${dominantElement}-elementti on hallitseva elementtisi, mikä vaikuttaa siihen, miten koet maailmaa ja reagoit siihen. Tämä yhdistelmä antaa sinulle vahvuuksia ja haasteita, jotka ovat osa henkilökohtaista kasvusi.`,
    interpretation_type: 'overview'
  };
}

/**
 * Generate all interpretations for a chart
 */
export function generateChartInterpretations(chartData) {
  if (!chartData) return [];

  const interpretations = [];

  // Overview
  const overview = generateOverviewInterpretation(chartData);
  if (overview) interpretations.push(overview);

  // Sun sign
  if (chartData.sunSign) {
    const sun = generateSunSignInterpretation(chartData.sunSign);
    if (sun) interpretations.push(sun);
  }

  // Moon sign
  if (chartData.moonSign) {
    const moon = generateMoonSignInterpretation(chartData.moonSign);
    if (moon) interpretations.push(moon);
  }

  // Rising sign
  if (chartData.risingSign) {
    const rising = generateRisingSignInterpretation(chartData.risingSign);
    if (rising) interpretations.push(rising);
  }

  // Planetary positions
  if (chartData.positions) {
    const planetInts = generatePlanetaryInterpretations(chartData.positions);
    interpretations.push(...planetInts);
  }

  // Aspects
  if (chartData.aspects && chartData.aspects.length > 0) {
    const aspectInts = generateAspectInterpretations(chartData.aspects);
    interpretations.push(...aspectInts);
  }

  // Houses
  if (chartData.houses) {
    const houseInts = generateHouseInterpretations(chartData.houses, chartData.risingSign);
    interpretations.push(...houseInts);
  }

  return interpretations;
}
